import React from 'react';
import { X, Plus, Star, Clock, Tag, BookOpen } from 'lucide-react';
import { clsx } from 'clsx';
import MathRenderer from '../math/MathRenderer';

const SolutionPreview = ({ solution, onClose, onInsert }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-5 h-5 text-sigma-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{solution.title}</h2>
              <p className="text-sm text-gray-500 capitalize">{solution.type}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={onInsert}
              className="inline-flex items-center px-4 py-2 bg-sigma-600 text-white rounded-lg hover:bg-sigma-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Insert
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Meta Information */}
          <div className="flex flex-wrap gap-2 mb-6">
            {solution.subject && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {solution.subject}
              </span>
            )}
            
            {solution.gradeLevel && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                {solution.gradeLevel.replace('_', ' ')}
              </span>
            )}
            
            {solution.difficulty && (
              <span className={clsx(
                'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize',
                getDifficultyColor(solution.difficulty)
              )}>
                {solution.difficulty}
              </span>
            )}
            
            {solution.topic && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                <Tag className="w-3 h-3 mr-1" />
                {solution.topic}
              </span>
            )}
          </div>

          {/* Description */}
          {solution.description && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
              <p className="text-gray-700">{solution.description}</p>
            </div>
          )}

          {/* Mathematical Content */}
          {solution.mathContent && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Mathematical Expression</h3>
              <div className="p-6 bg-gray-50 rounded-lg text-center">
                <MathRenderer latex={solution.mathContent} />
              </div>
            </div>
          )}

          {/* Text Content */}
          {solution.content && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Solution</h3>
              <div className="prose prose-sm max-w-none">
                {solution.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-3 text-gray-700">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Steps (if available) */}
          {solution.steps && solution.steps.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Solution Steps</h3>
              <div className="space-y-4">
                {solution.steps.map((step, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-sigma-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        {step.title && (
                          <h4 className="font-medium text-gray-900 mb-1">{step.title}</h4>
                        )}
                        
                        {step.content && (
                          <p className="text-gray-700 mb-2">{step.content}</p>
                        )}
                        
                        {step.mathContent && (
                          <div className="p-3 bg-gray-50 rounded text-center">
                            <MathRenderer latex={step.mathContent} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Concepts */}
          {solution.relatedConcepts && solution.relatedConcepts.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Related Concepts</h3>
              <div className="flex flex-wrap gap-2">
                {solution.relatedConcepts.map((concept, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                  >
                    {concept}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Prerequisites */}
          {solution.prerequisites && solution.prerequisites.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Prerequisites</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {solution.prerequisites.map((prerequisite, index) => (
                  <li key={index}>{prerequisite}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              <span>{solution.estimatedTime || '5'} min read</span>
            </div>
            
            {solution.rating && (
              <div className="flex items-center">
                <Star className="w-4 h-4 text-yellow-500 mr-1" />
                <span>{solution.rating.toFixed(1)} ({solution.ratingCount || 0} reviews)</span>
              </div>
            )}
            
            <span>{solution.views || 0} views</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            
            <button
              onClick={onInsert}
              className="px-4 py-2 bg-sigma-600 text-white rounded-lg hover:bg-sigma-700 transition-colors"
            >
              Insert Solution
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionPreview;